import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";
import type { CandidateGroup, MetaAddress, MetaBadge, MetaOrder } from "@/services/meta-orders";
import type { ImportResult } from "@/services/shipping";

export type MetaCandidates = { groups: CandidateGroup[]; skippedStores: { id: string; name: string }[] };
export type MetaCredential = { id: string; name: string; storeName: string | null; color: string | null };

const post = <T>(url: string, body?: unknown) =>
  fetchJson<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

export function useMetaCandidates() {
  return useQuery({
    queryKey: ["meta-candidates"],
    queryFn: () => fetchJson<MetaCandidates>("/api/meta-orders/candidates"),
  });
}

export function useMetaOrders() {
  return useQuery({
    queryKey: ["meta-orders"],
    queryFn: () => fetchJson<MetaOrder[]>("/api/meta-orders"),
  });
}

export function useMetaCredentials(enabled = true) {
  return useQuery({
    queryKey: ["meta-credentials"],
    queryFn: () => fetchJson<MetaCredential[]>("/api/meta-orders/credentials"),
    enabled,
  });
}

/** Oznake (kandidat / član meta narudžbe) za narudžbe na jednoj stranici liste — jedan upit po stranici. */
export function useMetaBadges(storeId: string | null, orderIds: (string | number)[]) {
  const ids = orderIds.map(String).sort();
  return useQuery({
    queryKey: ["meta-badges", storeId, ids],
    queryFn: () =>
      fetchJson<Record<string, MetaBadge>>(
        `/api/meta-orders/badges?storeId=${encodeURIComponent(storeId ?? "")}&orderIds=${ids.join(",")}`,
      ),
    enabled: !!storeId && ids.length > 0,
  });
}

/**
 * Prefiksi keševa koje mijenja svaka akcija nad meta narudžbom: kandidati i oznake, narudžbe
 * (poštarina, status), te nalog/tracking koji vide naljepnice, pick/pack lookup i otkupnine.
 */
const META_AFFECTED_KEYS = [
  "meta-orders",
  "meta-candidates",
  "meta-badges",
  "orders",
  "order",
  "order-shipments",
  "order-shipments-bulk",
  "label-orders",
  "label-orders-all",
  "fulfillment-lookup",
  "cod-ledger",
  "cod-summary",
] as const;

/**
 * `onSettled` vraća promise, pa `mutateAsync`/`isPending` traju dok se aktivni upiti ne osvježe —
 * npr. kartica kandidata ostaje zaključana dok spojena grupa ne nestane iz liste.
 */
function useMetaMutation<V, R>(fn: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => Promise.all(META_AFFECTED_KEYS.map((key) => qc.invalidateQueries({ queryKey: [key] }))),
  });
}

export function useMergeOrders() {
  return useMetaMutation((body: { orders: { storeId: string; orderId: string }[]; address: MetaAddress }) =>
    post<MetaOrder>("/api/meta-orders", body),
  );
}

export function useRetryMetaSync() {
  return useMetaMutation((id: number) => post<MetaOrder>(`/api/meta-orders/${id}/retry-sync`));
}

export function useRemoveMetaMember() {
  return useMetaMutation(({ id, storeId, orderId }: { id: number; storeId: string; orderId: string }) =>
    post<MetaOrder>(`/api/meta-orders/${id}/remove-member`, { storeId, orderId }),
  );
}

export function useDissolveMeta() {
  return useMetaMutation((id: number) => post<MetaOrder>(`/api/meta-orders/${id}/dissolve`));
}

export function useResendMetaEmail() {
  return useMetaMutation((id: number) => post<{ sent: boolean; error?: string }>(`/api/meta-orders/${id}/resend-email`));
}

export function useCreateMetaShipment() {
  return useMetaMutation(
    ({ id, ...body }: { id: number; credentialId: string; numberOfPackages: number; allowOpening: boolean }) =>
      post<ImportResult>(`/api/meta-orders/${id}/shipment`, body),
  );
}

export function useAttachMetaTracking() {
  return useMetaMutation(({ id, trackingNumber }: { id: number; trackingNumber: string }) =>
    post<{ trackingNumber: string }>(`/api/meta-orders/${id}/shipment/attach`, { trackingNumber }),
  );
}

export function useResetMetaShipping() {
  return useMetaMutation((id: number) => post<{ count: number }>(`/api/meta-orders/${id}/shipment/reset`));
}
